import React from 'react'
import { Button, Grid, CircularProgress, Tooltip, IconButton } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import InfoIcon from '@mui/icons-material/Info'
import '../live-data-view-style.scss'

const DataRow = ({ label, value, unit }) => (
    <div className="data-row">
        <span className="data-label">{label}</span>
        <span className="data-value">{value}{unit && <span className="unit">{unit}</span>}</span>
    </div>
)

const DataCard = ({ title, subtitle, statusIcon, statusText, children }) => (
    <div className="data-card">
        <div className="card-header">
            <div>
                <h3 className="card-title">{title}</h3>
                {subtitle && <p className="card-subtitle">{subtitle}</p>}
            </div>
            {statusIcon && (
                <div className="card-status">
                    <img src={statusIcon} alt="status" className="status-icon" />
                    {statusText && <span className="status-text">{statusText}</span>}
                </div>
            )}
        </div>
        <div className="card-body">{children}</div>
    </div>
)

const LiveDataViewDesktop = ({ logic }) => {
    const { fetchedData, calculatedData, calculatedInverters, availableDevices, loading, handleRefresh } = logic

    const infoTooltipContent = (
        <div style={{ padding: '8px', fontSize: '12px', lineHeight: '1.5' }}>
            <p><strong>Note 1:</strong> 'undefined' data values mean the logger didn't send data for that timestamp; it doesn't always mean the device is offline.</p>
            <p style={{ marginTop: '8px' }}><strong>Note 2:</strong> 'Offline' status is assigned if the last two consecutive data points are missing OR if the latest available data value is 0. Otherwise, it is 'Online'.</p>
            <p style={{ marginTop: '8px' }}><strong>Note 3:</strong> Status Colors: Green (Online), Red (Offline).</p>
            <p style={{ marginTop: '8px' }}><strong>Note 4:</strong> 'Since X days ago' indicates the data is from X days ago because today's data is missing.</p>
        </div>
    )

    return (
        <div className="live-data-view-container">
            <div className="live-data-content">
                {loading && <div className="loading-overlay"><CircularProgress size={40} /><span>Updating live data...</span></div>}
                <section className="data-section fetched-section">
                    <div className="section-header space-between space-after-section">
                        <div><h2 className="section-title">Fetched (Real-Time Data)</h2><p className="section-description">Latest device readings from IoT sensors</p></div>
                        <div className="section-actions">
                            <Tooltip title={infoTooltipContent} arrow placement="left">
                                <IconButton size="small" sx={{ mr: 1, color: '#666' }}>
                                    <InfoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={loading} className="refresh-button">{loading ? 'Refreshing...' : 'Refresh'}</Button>
                        </div>
                    </div>
                    <div className="section-grid">
                        <Grid container spacing={2}>
                            {fetchedData.inverters.length > 0 ? fetchedData.inverters.map(inv => (
                                <Grid item xs={12} sm={6} key={inv.inverterID}>
                                    <DataCard title={inv.inverterID} subtitle={inv.name} statusIcon={inv.statusIcon} statusText={inv.status}>
                                        {inv.model && <DataRow label="Model" value={inv.model} />}
                                        <DataRow
                                            label={inv.power.gapDays > 1 ? `Power (Since ${inv.power.gapDays} days ago)` : "Power"}
                                            value={inv.power.value} unit={` ${inv.power.unit}`}
                                        />
                                        <DataRow label="Status" value={inv.status} />
                                        <DataRow
                                            label={inv.todayGeneration.gapDays > 1 ? `Today Generation (Since ${inv.todayGeneration.gapDays} days ago)` : "Today Generation"}
                                            value={inv.todayGeneration.value} unit={` ${inv.todayGeneration.unit}`}
                                        />
                                    </DataCard>
                                </Grid>
                            )) : <Grid item xs={12}><div className="data-card no-data"><p>No inverter data available</p></div></Grid>}

                            {availableDevices.sensors > 0 && (
                                <Grid item xs={12} sm={6}>
                                    <DataCard title={fetchedData.sensor.sensorID} subtitle={fetchedData.sensor.name} statusIcon={fetchedData.sensor.statusIcon} statusText={fetchedData.sensor.status}>
                                        <DataRow label="Model Name" value={fetchedData.sensor.modelName} />
                                        <DataRow
                                            label={fetchedData.sensor.temperature.gapDays > 1 ? `Temperature (Since ${fetchedData.sensor.temperature.gapDays} days ago)` : "Temperature"}
                                            value={fetchedData.sensor.temperature.value} unit={` ${fetchedData.sensor.temperature.unit}`}
                                        />
                                        <DataRow
                                            label={fetchedData.sensor.irradiance.gapDays > 1 ? `Irradiance (Since ${fetchedData.sensor.irradiance.gapDays} days ago)` : "Irradiance"}
                                            value={fetchedData.sensor.irradiance.value} unit={` ${fetchedData.sensor.irradiance.unit}`}
                                        />
                                        <DataRow
                                            label={fetchedData.sensor.irradiation.gapDays > 1 ? `Irradiation (Since ${fetchedData.sensor.irradiation.gapDays} days ago)` : "Irradiation"}
                                            value={fetchedData.sensor.irradiation.value} unit={` ${fetchedData.sensor.irradiation.unit}`}
                                        />
                                    </DataCard>
                                </Grid>
                            )}
                        </Grid>
                    </div>
                </section>
                <section className="data-section calculated-section">
                    <div className="section-header"><h2 className="section-title">Calculated (Daily Comparison)</h2><p className="section-description">Previous day vs current day energy values</p></div>
                    <div className="section-grid">
                        <Grid container spacing={2}>
                            {availableDevices.energyMeters > 0 && (
                                <Grid item xs={12} sm={6}>
                                    <DataCard title={calculatedData.meter.meterID} subtitle={calculatedData.meter.name} statusIcon={calculatedData.meter.statusIcon} statusText={calculatedData.meter.status}>
                                        <DataRow label="Status" value={calculatedData.meter.status} />
                                        <DataRow
                                            label={calculatedData.meter.lastEnergyExport.gapDays > 1
                                                ? `Last Energy Export Till (Since ${calculatedData.meter.lastEnergyExport.gapDays} days ago)`
                                                : "Last Energy Export Till"}
                                            value={calculatedData.meter.lastEnergyExport.value}
                                            unit={` ${calculatedData.meter.lastEnergyExport.unit}`}
                                        />
                                        <DataRow label="Current Energy Export Till" value={calculatedData.meter.current.value} unit={` ${calculatedData.meter.current.unit}`} />
                                        <DataRow label="Multiplying Factor" value={calculatedData.meter.multiplyingFactor.value} unit={` ${calculatedData.meter.multiplyingFactor.unit}`} />
                                        <DataRow label="Today Generation" value={calculatedData.meter.todayGeneration.value} unit={` ${calculatedData.meter.todayGeneration.unit}`} />
                                    </DataCard>
                                </Grid>
                            )}
                            {calculatedInverters.map(inv => (
                                <Grid item xs={12} sm={6} key={inv.inverterID}>
                                    <DataCard title={inv.inverterID} subtitle={inv.name} statusIcon={inv.statusIcon} statusText={inv.status}>
                                        {inv.model && <DataRow label="Model" value={inv.model} />}
                                        <DataRow label="Status" value={inv.status} />
                                        <DataRow
                                            label={inv.last.gapDays > 1
                                                ? `Last Inverter Till Gen (Since ${inv.last.gapDays} days ago)`
                                                : "Last Inverter Till Generation"}
                                            value={inv.last.value}
                                            unit={` ${inv.last.unit}`}
                                        />
                                        <DataRow label="Current Inverter Till Generation" value={inv.current.value} unit={` ${inv.current.unit}`} />
                                        <DataRow label="Today Generation" value={inv.todayGeneration.value} unit={` ${inv.todayGeneration.unit}`} />
                                    </DataCard>
                                </Grid>
                            ))}
                        </Grid>
                    </div>
                </section>
            </div >
        </div >
    )
}

export default LiveDataViewDesktop
